import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Box, 
  Button, 
  Tabs,
  Tab,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import LaunchIcon from '@mui/icons-material/Launch';
import DownloadIcon from '@mui/icons-material/Download';
import MethodologyDocs from './MethodologyDocs';

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`resources-tabpanel-${index}`}
      aria-labelledby={`resources-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ResourcesHome = () => {
  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();

  // Reset tab based on URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    if (tab === 'presentation-templates') setTabValue(0);
    else if (tab === 'quick-reference-guides') setTabValue(1);
    else if (tab === 'documentation') setTabValue(2);
  }, [location]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Presentation templates data
  const presentationTemplates = [
    {
      title: "School-Specific Leadership Presentation",
      description: "Understand the population and competitive dynamics impacting a school's enrollment trends in support of calculating long-term enrollment projections.",
      status: "available",
      googleSlidesUrl: "https://docs.google.com/presentation/d/1RtEGLMXRdF7ig3zkk2Djq6r7_SfdN11_TfL3IEfTm2M/copy",
      powerPointUrl: "/templates/edc-school-leadership-presentation.pptx"
    },
    {
      title: "Portfolio Leadership Presentation",
      description: "Understand how population and competitive dynamics impact the overall portfolio, including identifying specific areas of strength, risk, and opportunity.",
      status: "available",
      googleSlidesUrl: "https://docs.google.com/presentation/d/176O5qcI28c1l3loGg1cr77NHYhD-f2jM_07VvwtaK2U/copy",
      powerPointUrl: "/templates/edc-portfolio-leadership-presentation.pptx"
    },
    {
      title: "New School Site Evaluation Presentation",
      description: "Evaluate a potential new school site by understanding the population and competitive dynamics that would impact long-term enrollment target setting.",
      status: "available",
      googleSlidesUrl: "https://docs.google.com/presentation/d/1I5Dh5jVGo7X6bJxVZdC1hiv6kg5qt7Xuy1gu1U6L8Y8/copy",
      powerPointUrl: "/templates/edc-site-evaluation-presentation.pptx"
    }
  ];

  // Quick Reference Guides data
  const quickReferenceGuides = [
    {
      title: "What's happening in your area?",
      description: "Use the Geographic Explorer to understand local population size, neighborhood dynamics, and how community factors may affect your schools.",
      status: "available",
      url: "https://drive.google.com/file/d/1hcjWuRd1l5mMLi3I9SPbgC10TRdjCtEU/view"
    },
    {
      title: "What's happening with demographics?",
      description: "The Geographic Explorer highlights shifts in age groups, household makeup, and other key indicators that influence future enrollment.",
      status: "available",
      url: "https://drive.google.com/file/d/1sRmKrKSeSkPghDdvC5oeGxCUuXGiyD9E/view"
    },
    {
      title: "What's happening with competition?",
      description: "With the Competitive Explorer, see which schools are drawing students, compare market share, and identify emerging competitors.",
      status: "available",
      url: "https://drive.google.com/file/d/1u7I3KyTKmvwzeTG0rM9RbnucT8Pkwsa_/view?usp=sharing"
    },
    {
      title: "What's happening with projections?",
      description: "Rely on the Projections Explorer to view 1-, 3-, and 5-year scenarios for school- and grade-level enrollment, so you can plan with foresight.",
      status: "available",
      url: "https://drive.google.com/file/d/18oRcQoQr4eh5QeKvVxST9SifQFX7wwgb/view?usp=sharing"
    },
    {
      title: "Where do your students come from?",
      description: "Use the Student Catchment Explorer to understand where students live, how far they travel, and where opportunities may exist.",
      status: "available",
      url: "https://drive.google.com/file/d/1l20ESYwkgpvKUKPoEhEoofZwb9VhnR9u/view?usp=sharing"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Resources
      </Typography>
      
      {/* Section descriptor */}
      <Box sx={{ my: 3 }}>
        <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Access presentation templates, quick reference guides, and documentation to help you share insights 
          and communicate findings effectively with your team and stakeholders.
        </Typography>
      </Box>
      
      {/* Tabs navigation */}
      <Paper sx={{ borderRadius: 1, boxShadow: 1, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'uppercase',
              fontWeight: 'medium',
              fontSize: '0.875rem',
            }
          }}
        >
          <Tab 
            icon={<CoPresentOutlinedIcon />} 
            iconPosition="start" 
            label="Presentation Templates" 
            id="resources-tab-0"
            aria-controls="resources-tabpanel-0"
            sx={{ py: 2 }}
          />
          <Tab 
            icon={<ArticleIcon />} 
            iconPosition="start" 
            label="Quick Reference Guides" 
            id="resources-tab-1"
            aria-controls="resources-tabpanel-1"
            sx={{ py: 2 }}
          />
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label="Documentation" 
            id="resources-tab-2"
            aria-controls="resources-tabpanel-2"
            sx={{ py: 2 }}
          />
        </Tabs>
        
        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          <Card sx={{ boxShadow: 0, pb: 4 }}>
            <CardContent sx={{ pb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={1}>
                  <Box display="flex" justifyContent="center">
                    <CoPresentOutlinedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={11}>
                  <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                    Presentation Templates
                  </Typography>
                  <Typography variant="body1">
                    Access annotated presentations for various use cases that include opportunities to dig deeper and contextualize findings against your network or national peers.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
            
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0', px: 3 }}>
                <Grid container spacing={3}>
                  {presentationTemplates.map((template, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {template.title}
                            </Typography>
                            {template.status === 'available' ? (
                              <Chip 
                                label="Available" 
                                size="small" 
                                color="success" 
                                variant="filled"
                              />
                            ) : (
                              <Chip 
                                label="Coming Soon" 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Button 
                            variant={template.status === 'available' && template.googleSlidesUrl ? "contained" : "outlined"}
                            disabled={template.status !== 'available' || !template.googleSlidesUrl}
                            startIcon={<LaunchIcon />}
                            fullWidth
                            onClick={() => {
                              if (template.googleSlidesUrl) {
                                window.open(template.googleSlidesUrl, '_blank');
                              }
                            }}
                          >
                            Google Slides Template
                          </Button>
                          <Button 
                            variant={template.status === 'available' && template.powerPointUrl ? "contained" : "outlined"}
                            disabled={template.status !== 'available' || !template.powerPointUrl}
                            startIcon={<DownloadIcon />}
                            fullWidth
                            onClick={() => {
                              if (template.powerPointUrl) {
                                // Create a temporary link element for download
                                const link = document.createElement('a');
                                link.href = template.powerPointUrl;
                                link.download = template.powerPointUrl.split('/').pop();
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            PowerPoint Template
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
            </Box>
          </Card>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Card sx={{ boxShadow: 0, pb: 4 }}>
            <CardContent sx={{ pb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={1}>
                  <Box display="flex" justifyContent="center">
                    <ArticleIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={11}>
                  <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                    Quick Reference Guides
                  </Typography>
                  <Typography variant="body1">
                    Short, practical guides that walk you through the tool's core features—so you can answer the right questions quickly and confidently.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
            
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0', px: 3 }}>
                <Grid container spacing={3}>
                  {quickReferenceGuides.map((guide, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {guide.title}
                            </Typography>
                            {guide.status === 'available' ? (
                              <Chip 
                                label="Available" 
                                size="small" 
                                color="success" 
                                variant="filled"
                              />
                            ) : (
                              <Chip 
                                label="Coming Soon" 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {guide.description}
                          </Typography>
                        </CardContent>
                        <Box sx={{ p: 2 }}>
                          <Button 
                            variant={guide.status === 'available' && guide.url ? "contained" : "outlined"}
                            disabled={guide.status !== 'available' || !guide.url}
                            startIcon={<LaunchIcon />}
                            fullWidth
                            onClick={() => {
                              if (guide.url) {
                                window.open(guide.url, '_blank');
                              }
                            }}
                          >
                            View Guide
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
            </Box>
          </Card>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Card sx={{ boxShadow: 0, pb: 4 }}>
            <CardContent sx={{ pb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={1}>
                  <Box display="flex" justifyContent="center">
                    <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={11}>
                  <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                    Documentation
                  </Typography>
                  <Typography variant="body1">
                    Detailed methodology and technical documentation for our enrollment planning tools.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
            
            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              <Grid container spacing={3} sx={{ px: 3 }}>
                {/* Preview of methodology documentation topics */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Key Topics
                  </Typography>
                  <List>
                    {['Purpose', 'Methodology Summary', 'Process Summary', 'Projection Confidence Levels', 'Cohort Survival Rate'].map((topic, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <InfoIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={topic} />
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="contained" 
                      component={Link}
                      to="/resources/methodology"
                      state={{ from: location.pathname }}
                      startIcon={<DescriptionIcon />}
                    >
                      View Full Documentation
                    </Button>
                  </Box>
                </Grid>
                
                {/* Quick view sample of documentation */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      How we calculate projections:
                    </Typography>
                    <Typography variant="body2">
                      The tool uses historical data from NCES and the most recent school year to balance
                      longer-term history with recent trends. We create three scenarios: high, midpoint, and
                      low to allow you to see a range of potential outcomes.
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                      <WarningIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Projections should be treated as a starting point for internal enrollment planning discussions.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </TabPanel>
      </Paper>
    </Container>
  );
};

const Resources = () => {
  return (
    <Routes>
      <Route path="/" element={<ResourcesHome />} />
      <Route path="/methodology" element={<MethodologyDocs />} />
    </Routes>
  );
};

export default Resources;